import { Directive, EventEmitter, Input, Output } from '@angular/core';
import { IMap } from '../interfaces/imap';
import { MapLatLng } from '../models/map.latlng';
import { MapBounds } from '../models/map.bounds';
import { MapMarker } from '../models/map.marker';
import { MapPolyline } from '../models/map.polyline';
import { MapPolylinePoint } from '../models/map.polylinePoint';
import { MapCircle } from '../models/map.circle';
import { MapPolygonPoint } from '../models/map.polygonPoint';
import { MapPolygon } from '../models/map.polygon';
import { HeatMap } from '../models/map.heatMap';
import { MapSearch } from '../models/map.search';
import { MapPoint } from '../models/map.point';

@Directive({
})
export class MapBaseDirective implements IMap {
    protected map: any; // Mapa
    public geoCoder: any; // Para busquedas sobre el mapa
    protected bounds: MapBounds | undefined; // Marco del mapa

    // Outputs del mapa
    @Output()
    mapReady = new EventEmitter<any>();
    @Output()
    mapClick = new EventEmitter<MapLatLng>();
    @Output()
    mapMouseMove = new EventEmitter<MapLatLng>();
    @Output()
    boundsChange = new EventEmitter<MapBounds>();
    @Output()
    zoomChange = new EventEmitter<number>();
    @Output()
    markerClick = new EventEmitter<number>();
    @Output()
    markerClickGeoJson = new EventEmitter<MapMarker>();
    @Output()
    markerDragEnd = new EventEmitter<number>();
    @Output()
    markerMove = new EventEmitter<number>();
    @Output()
    polylineClick = new EventEmitter<MapPolylinePoint>();
    @Output()
    polygonClick = new EventEmitter<MapPolygonPoint>();
    @Output()
    polygonDragEnd = new EventEmitter<MapPolygonPoint>();
    @Output()
    circleClick = new EventEmitter<MapCircle>();
    @Output()
    circleDragEnd = new EventEmitter<MapCircle>();
    @Output()
    circleRadiusChanged = new EventEmitter<MapCircle>();
    @Output()
    mapSearch = new EventEmitter<MapSearch>();

    // Inputs del mapa
    @Input()
    lang = 'es-ES'; // Idioma por defecto
    @Input()
    searchCountry = 'es'; // Pais por defecto a efectos de búsquedas
    @Input()
    texts = new Map<string, string>(); // Textos del idioma cargado
    @Input()
    cartoType = 'raster';
    @Input()
    searchBounds = ''; // Marco geográfico a efectos de búsquedas
    @Input()
    noSearch = false; // Permite indicar que no queremos buscador

    // Configuración por defecto del mapa
    public mapType = 'roadmap';
    public zoom = 6;
    public center = { // Madrid
        lat: 40.4165001,
        lng: -3.7025599
    };

    constructor() {
    }

    // Indica cuando el mapa está listo
    onMapReady(map: any) {
        this.setMapType(this.mapType);
        this.setZoom(this.zoom);
        this.setCenter(new MapLatLng(this.center.lat, this.center.lng));
        this.mapReady.emit(map);
    }

    // Cuando se mueve el puntero sobre el mapa
    onMapClick(point: MapLatLng) {
        this.mapClick.emit(point);
    }

    // Cada vez que se pincha sobre el mapa
    onMapMouseMove(point: MapLatLng) {
        this.mapMouseMove.emit(point);
    }

    // Cada vez que cambia el encuadre
    onBoundsChange(bounds: MapBounds) {
        if (this.bounds !== bounds) {
            this.bounds = bounds;
            this.boundsChange.emit(bounds);
        }
    }

    // Cada vez que cambia el nivel de zoom
    onZoomChange(zoom: number) {
        if (this.zoom !== zoom) {
            this.zoom = zoom;
            this.zoomChange.emit(zoom);
        }
    }

    // Cada vez que se pincha sobre un marcador
    onMarkerClick(id: number) {
        this.markerClick.emit(id);
    }

    // Cada vez que se pincha sobre un marcador de un GeoJSON
    onMarkerClickGeoJson(marker: MapMarker) {
        this.markerClickGeoJson.emit(marker);
    }

    // Cada vez que se arrastra y se deja caer un marcador
    onMarkerDragEnd(markerInfo: any) {
        this.markerDragEnd.emit(markerInfo);
    }

    // Cada vez que se mueve un marcador
    onMarkerMove(markerInfo: any) {
        this.markerMove.emit(markerInfo);
    }

    // Cada vez que se pincha sobre un punto de una polilinea
    onPolylineClick(point: MapPolylinePoint) {
        this.polylineClick.emit(point);
    }

    // Cada vez que se pincha sobre un punto de un polígono
    onPolygonClick(point: MapPolygonPoint) {
        this.polygonClick.emit(point);
    }

    // Cada vez que se arrastra un punto de un polígono
    onPolygonDragEnd(point: MapPolygonPoint) {
        this.polygonDragEnd.emit(point);
    }

    // Cada vez que se pincha sobre un círculo
    onCircleClick(circle: MapCircle) {
        this.circleClick.emit(circle);
    }

    // Cada vez que se arrastra y se deja caer un circulo
    onCircleDragEnd(circleInfo: any) {
        this.circleDragEnd.emit(circleInfo);
    }

    // Cuando se modifica el radio de un círculo
    onCircleRadiusChanged(circle: any) {
        this.circleRadiusChanged.emit(circle);
    }

    // Establece el tipo de mapa
    setMapType(type: string) {
        this.map.setMapType(type);
    }

    // Redimensiona el mapa para que se adapte al tamaño del div que lo contiene
    resize() {
        this.map.resize();
    }

    // Establece el centro del mapa
    setCenter(center: MapLatLng) {
        this.map.setCenter(center);
    }

    // Establece el nivel de zoom
    setZoom(zoom: number) {
        this.map.setZoom(zoom);
    }

    // Este método lo implementan las clases derivadas
    addMarker(id: number, title: string, label: string, content: string, position: MapLatLng,
        icon: string, zIndex: number, drag: boolean, visible: boolean,
        minZoom: number, maxZoom: number, cluster: boolean, clusterV2: boolean): any {
        return null;
    }

    // Este método lo implementan las clases derivadas
    removeMarker(marker: any) {
    }

    // Este método lo implementan las clases derivadas
    addCluster(id: number, maxZoom: number): any {
        return null;
    }

    // Este método lo implementan las clases derivadas
    removeCluster(cluster: any) {
    }

    // Este método lo implementan las clases derivadas
    repaintCluster(cluster: any) {
    }

    // Este método lo implementan las clases derivadas
    getBounds(): any {
        return null;
    }

    // Este método lo implementan las clases derivadas
    fitTo(mapBounds: MapBounds) {
    }

    // Este método lo implementan las clases derivadas
    panTo(point: MapLatLng) {
    }

    // Este método lo implementan las clases derivadas
    addPolyline(id: number, color: string, opacity: number, weight: number, clickable: boolean): any {
        return null;
    }

    // Este método lo implementan las clases derivadas
    addPolylinePoint(id: number, polyline: MapPolyline, content: string, position: MapLatLng): any {
        return null;
    }

    // Este método lo implementan las clases derivadas
    removePolyline(polyline: MapPolyline) {
    }

    // Este método lo implementan las clases derivadas
    addPolygon(id: number, strokeColor: string, strokeOpacity: number, weight: number, fillColor: string,
        fillOpacity: number, editable: boolean, hole: boolean, content: string): any {
        return null;
    }

    // Este método lo implementan las clases derivadas
    addPolygonPoint(id: number, polyline: MapPolygon, content: string, position: MapLatLng, dataMoldel: any): any {
        return null;
    }

    // Este método lo implementan las clases derivadas
    removePolygon(polygon: MapPolygon) {
    }

    // Este método lo implementan las clases derivadas
    addCircle(id: number, content: string, strokeColor: string, strokeOpacity: number,
        strokeWeight: number, fillColor: string, fillOpacity: number, point: MapLatLng,
        radius: number, editable: boolean, draggable: boolean): any {
        return null;
    }

    // Este método lo implementan las clases derivadas
    addHeatMap(id: number, points: MapLatLng[], markers: MapMarker[], radius: number, dissipating: boolean,
        maxIntensity: number, opacity: number, gradient: any[]): any {
        return null;
    }

    // Este método lo implementan las clases derivadas
    removeHeatMap(heatMap: HeatMap) {
    }

    // Este método lo implementan las clases derivadas
    removeCircle(circle: MapCircle) {
    }

    // Este método lo implementan las clases derivadas
    loadGeoJson(file: string, options: any = null): any {
    }

    // Este método lo implementan las clases derivadas
    addGeoJson(geoJson: any, options: any = null, file: any = null): any {
    }

    // Este método lo implementan las clases derivadas
    removeGeoJson(file: any): void {
    }

    // Notifica una búsqueda en el mapa
    onMapSearch(position: MapLatLng, place: any) {
        this.mapSearch.emit(new MapSearch(position, place));
    }

    // Este método lo implementan las clases derivadas
    setMousePointer(pointer: string) {
    }

    // Este método lo implementan las clases derivadas
    getProjection(): any {
    }

    // Este método lo implementan las clases derivadas
    fromLatlngToPoint(position: MapLatLng): any {
    }

    // Este método lo implementan las clases derivadas
    fromPointToLatlng(point: MapPoint): any {
    }

    // Este método lo implementan las clases derivadas
    itinerary(from: string, to: string, fromPos: MapLatLng, toPos: MapLatLng, result: string): any {
    }

    // Este método lo implementan las clases derivadas
    latLngToScreenPoint(position: MapLatLng): any {
    }

    // Este método lo implementan las clases derivadas
    screenPointToLatLng(point: MapPoint): any {
    }

    // Este método lo implementan las clases derivadas
    loadKml(file: string): any {
    }

    // Este método lo implementan las clases derivadas
    addKml(file: any): any {
    }

    // Este método lo implementan las clases derivadas
    removeKml(file: any): void {
    }

    // Este método lo implementan las clases derivadas
    closeInfowindowMarker(): void {
    }

    // Este método lo implementan las clases derivadas
    getMaxZoom(): number {
        return 0;
    }

    // Para traducir textos
    translate(text: string, defText: string = '') {
        const res = this.texts.get(text);
        if (!res && defText !== '') {
            text = defText;
        }
        return res ? res : text;
    }

    // Distancia punto a segmento
    distancePointToSegment(sx: number, sy: number, ex: number, ey: number, px: number, py: number): number {
        const dx = ex - sx;
        const dy = ey - sy;
        const l2 = dx * dx + dy * dy;
        if (l2 == 0) {
            return this.dist(px, py, sx, sy);
        }
        let t = ((px - sx) * dx + (py - sy) * dy) / l2;
        t = Math.max(0, Math.min(1, t));
        return this.dist(px, py, sx + t * dx, sy + t * dy);
    }

    // Distancia entre dos puntos (pitágoras)
    dist(vx: number, vy: number, wx: number, wy: number): number {
        return Math.sqrt(Math.pow(vx - wx, 2) + Math.pow(vy - wy, 2));
    }

    distanceGeo(lat1: number, lng1: number, lat2: number, lng2: number): number {
        const rad = ((x: number) => {
            return x * Math.PI / 180;
        });
        const R = 6378.137; //Radio de la tierra en km 
        const dLat = rad(lat2 - lat1);
        const dLong = rad(lng2 - lng1);
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(rad(lat1)) *
            Math.cos(rad(lat2)) * Math.sin(dLong / 2) * Math.sin(dLong / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const d = R * c * 1000; // Paso a metros
        return d;
    }

}
